import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { EmployeeNotification, Ticket, TicketStatus } from './asignaciones.service';
import { buildGatewaySocketOptions } from '../utils/gateway-socket-options';
import { getWebsocketBaseUrl } from '../utils/websocket-base-url';

export interface TicketStatusChangedEvent {
  id: number;
  status: TicketStatus;
  order?: number;
  completedAt?: string | null;
  actorEmployeeId?: number | null;
  previousStatus?: TicketStatus;
}

export interface TicketDeletedEvent {
  id: number;
}

export interface NotificationCreatedEvent {
  employeeId: number;
  notification: EmployeeNotification;
}

@Injectable({
  providedIn: 'root',
})
export class TicketsWebSocketService {
  private socket: Socket | null = null;
  private connected$ = new BehaviorSubject<boolean>(false);
  private joinedEmployeeId: number | null = null;
  private refCount = 0;
  private readonly baseUrl = getWebsocketBaseUrl();

  /** Incrementa usos y abre el socket si hace falta. */
  connect(employeeId?: number): void {
    this.refCount += 1;
    if (employeeId) {
      this.joinedEmployeeId = employeeId;
    }

    if (this.socket?.connected) {
      if (employeeId) this.joinEmployee(employeeId);
      return;
    }

    if (this.socket && !this.socket.connected) {
      this.socket.connect();
      return;
    }

    try {
      const { options } = buildGatewaySocketOptions();
      this.socket = io(`${this.baseUrl}/tickets`, options);

      this.socket.on('connect', () => {
        this.connected$.next(true);
        this.socket?.emit('join');
        if (this.joinedEmployeeId) {
          this.joinEmployee(this.joinedEmployeeId);
        }
      });

      this.socket.on('disconnect', () => this.connected$.next(false));
      this.socket.on('connect_error', (error) => {
        console.error('tickets socket connect_error:', error?.message ?? error);
        this.connected$.next(false);
      });
    } catch (error) {
      console.error('Error connecting to tickets socket:', error);
      this.connected$.next(false);
    }
  }

  joinEmployee(employeeId: number): void {
    this.joinedEmployeeId = employeeId;
    if (!this.socket?.connected) return;
    this.socket.emit('joinEmployee', { employeeId });
  }

  /** Decrementa usos; solo cierra el socket cuando nadie lo usa. */
  disconnect(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount > 0 || !this.socket) return;

    if (this.joinedEmployeeId) {
      this.socket.emit('leaveEmployee', { employeeId: this.joinedEmployeeId });
    }
    this.socket.emit('leave');
    this.socket.disconnect();
    this.socket = null;
    this.connected$.next(false);
  }

  onTicketCreated(): Observable<Ticket> {
    return this.fromEvent<Ticket>('ticket_created');
  }

  onTicketUpdated(): Observable<Ticket> {
    return this.fromEvent<Ticket>('ticket_updated');
  }

  onTicketStatusChanged(): Observable<TicketStatusChangedEvent> {
    return this.fromEvent<TicketStatusChangedEvent>('ticket_status_changed');
  }

  onTicketDeleted(): Observable<TicketDeletedEvent> {
    return this.fromEvent<TicketDeletedEvent>('ticket_deleted');
  }

  onNotificationCreated(): Observable<NotificationCreatedEvent> {
    return this.fromEvent<NotificationCreatedEvent>('notification_created');
  }

  isConnected(): Observable<boolean> {
    return this.connected$.asObservable();
  }

  private fromEvent<T>(eventName: string): Observable<T> {
    return new Observable((observer) => {
      if (!this.socket) {
        console.error('Tickets socket not connected. Call connect() first.');
        return;
      }
      const handler = (data: T) => observer.next(data);
      this.socket.on(eventName, handler);
      return () => {
        this.socket?.off(eventName, handler);
      };
    });
  }
}
